import React from 'react';

export function AvailableChip({ product, style = {} }) {
  const calculateAvailability = () => {
    // Calculate next available day based on product inventory
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    
    // Use inventory data from the product object
    if (product?.inventory) {
      const inventory = product.inventory;
      
      // Check if same-day orders are allowed
      const sameDayAllowed = inventory.policy?.same_day_allowed || false;
      
      // Check if today has available inventory
      const todayInventory = inventory.scheduled?.find(row => 
        row.service_date === todayString && row.enabled && row.available_total > 0
      );
      
      // Also check for dates within 1 day of today (timezone handling)
      const todayPlusOne = new Date(today);
      todayPlusOne.setDate(today.getDate() + 1);
      const tomorrowString = todayPlusOne.toISOString().split('T')[0];
      
      const todayPlusOneInventory = inventory.scheduled?.find(row => 
        row.service_date === tomorrowString && row.enabled && row.available_total > 0
      );
      
      // Check if same-day orders are actually possible considering min_order_ahead policy
      const policy = product.policy;
      const canOrderToday = sameDayAllowed && 
        (todayInventory || todayPlusOneInventory) &&
        (!policy?.min_order_ahead || policy.min_order_ahead.value === 0);
      
      if (canOrderToday) {
        // Show "Available today" only if same-day orders are allowed, inventory is available, AND no min_order_ahead restriction
        return 'Available today';
      }
      
      // Find the next available day with inventory
      if (inventory.scheduled && Array.isArray(inventory.scheduled)) {
        const policy = product.policy;
        
        const nextAvailableDay = inventory.scheduled
          .filter(row => {
            // Check if date has available units
            if (row.available_total <= 0) return false;
            
            // Check if date is in the future
            if (row.service_date <= todayString) return false;
            
            // Check policy restrictions
            if (policy) {
              const orderDate = new Date(row.service_date);
              const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
              
              // Check min_order_ahead policy
              if (policy.min_order_ahead && policy.min_order_ahead.value) {
                const minDays = policy.min_order_ahead.value;
                if (daysDiff < minDays) return false;
              }
              
              // Check same_day_allowed policy
              if (!policy.same_day_allowed && daysDiff === 0) return false;
            }
            
            return true;
          })
          .sort((a, b) => new Date(a.service_date) - new Date(b.service_date))
          .find(row => new Date(row.service_date) > today);
        
        if (nextAvailableDay) {
          const nextDate = new Date(nextAvailableDay.service_date + 'T00:00:00');
          const options = { weekday: 'short', month: 'short', day: 'numeric' };
          const formattedDate = nextDate.toLocaleDateString('en-US', options);
          return `Available: ${formattedDate}`;
        }
      }
    }
    
    // Fallback: show tomorrow's date
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    const nextAvailableDay = tomorrow.toLocaleDateString('en-US', options);
    
    return `Available: ${nextAvailableDay}`;
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '4px',
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: 500,
        color: '#333',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(4px)',
        border: 'none',
        pointerEvents: 'none',
        ...style
      }}
    >
      {calculateAvailability()}
    </div>
  );
}

